import * as React from "react";
import ReactTooltip from 'react-tooltip';

import "./user-management-modal-layout.scss";

import {cx}                     from "emotion";
import {cs}                     from "@Common/react/chain-services";
import {consumeContext}         from "@Common/react/context";
import {UseState}               from "@Common/react/use-state";
import {needsConfirmation}      from "@Common/ui-components/needs-confirmation/needs-confirmation";
import {Responsive}             from "@Common/ui-components/responsive";
import {addURLParameters}       from "@Common/utils/add-url-parameter";
import {isEmpty}                from "@Common/utils/objects";
import {
    faAddressCard,
    faExternalLinkAlt,
    faLock,
    faPen,
    faShieldAlt,
}                               from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}        from "@fortawesome/react-fontawesome";
import {
    AGENT_LEVEL,
    BRANCH_LEVEL,
    COMPANY_LEVEL
}                               from "../../../../../common/logic/user-level";
import {mapFullNameAsInitials}  from "../user-management-card/user-management-card";

export const UserManagementModalLayout = ({user, agent, branchId, active, readOnly, dataUpdated, confirmCloseDialog, baseUrl, reload}) => cs(
    consumeContext("apis"),
    consumeContext("routerHistory"),
    // @ts-ignore
    ["responsive", ({}, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>],
    ["agentFullName", ({}, next) => next(agent.value &&
        [agent.value.agentNameFirst, agent.value.agentNameMiddle, agent.value.agentNameLast].filter(Boolean).join(" ")
    )],
    ["menuItems", ({}, next) => next(getMenuItems())],
    ["showingDeactivationWarning", (_, next) => UseState({initValue: false, next})],
    consumeContext("systemConstants"),
    ["daysToDelete", ({systemConstants}, next) => next(systemConstants?.value?.USER_DEACTIVATED_BECOMES_DELETED_DAYS)],
    ({agentFullName, apis, menuItems, showingDeactivationWarning, daysToDelete, routerHistory, responsive}) => (
        !isEmpty(agent.value) ? (
            <div className="user-management-modal-layout-sp5">
                {
                    responsive.ge("md")
                    ?
                    userInfoDesktop({
                        active,
                        agent,
                        branchId,
                        agentFullName,
                        apis,
                        baseUrl,
                        confirmCloseDialog,
                        dataUpdated,
                        daysToDelete,
                        menuItems,
                        readOnly,
                        reload,
                        routerHistory,
                        showingDeactivationWarning,
                        user
                    })
                    :
                    userInfoMobile({
                        active,
                        agent,
                        branchId,
                        agentFullName,
                        apis,
                        baseUrl,
                        confirmCloseDialog,
                        dataUpdated,
                        daysToDelete,
                        menuItems,
                        readOnly,
                        reload,
                        routerHistory,
                        showingDeactivationWarning,
                        user
                    })
                }
            </div>
            ) : (
            //If agent does not exist OR user does not have access to this agent, close the modal
            routerHistory.replace(baseUrl)
        )
    )
);

const userInfoDesktop = ({
    active,
    agent,
    branchId,
    agentFullName,
    apis,
    baseUrl,
    confirmCloseDialog,
    dataUpdated,
    daysToDelete,
    menuItems,
    readOnly,
    reload,
    routerHistory,
    showingDeactivationWarning,
    user
}) => {
    return (
        <>
            <ReactTooltip  id="user-management-modal-layout" place="bottom" delayShow={100}/>
            {headshot({agent})}
            {agentName({agentFullName})}
            {badges({agent, user})}
            <div className="menu">
                {navigation({active, agent, branchId, confirmCloseDialog, dataUpdated, baseUrl, menuItems, reload, routerHistory})}
                {readOnly?.companyAdmin && <div className="comment" xd-qa-type="label" xd-qa-name="Read Only">
                    Some settings are unavailable for users with a higher role
                </div>}
                {buttonActivate({agent, apis, readOnly, showingDeactivationWarning})}
                <div className="deactivation-warning">
                    {deactivationWarning({apis, agent, showingDeactivationWarning, daysToDelete})}
                </div>
            </div>
        </>
    )
}

const userInfoMobile = ({
    active,
    agent,
    branchId,
    agentFullName,
    apis,
    baseUrl,
    confirmCloseDialog,
    dataUpdated,
    daysToDelete,
    menuItems,
    readOnly,
    reload,
    routerHistory,
    showingDeactivationWarning,
    user
}) => {
    return (
        <>
            <div className="menu">
                <div className="header">
                    {headshot({agent})}
                    <div className="header-info">
                        {agentName({agentFullName})}
                        {badges({agent, user})}
                        {buttonActivate({agent, apis, readOnly, showingDeactivationWarning})}
                    </div>
                </div>
                <div className={
                    showingDeactivationWarning.value
                    ?
                    "deactivation-warning"
                    :
                    "deactivation-warning hidden"
                }>
                    {deactivationWarning({apis, agent, daysToDelete, showingDeactivationWarning})}
                </div>
                <div className="navigation">
                    {navigation({
                        active,
                        agent,
                        branchId,
                        baseUrl,
                        confirmCloseDialog,
                        dataUpdated,
                        menuItems,
                        reload,
                        routerHistory,
                        showText: false
                        })}
                </div>
            </div>
        </>
    )
}

const badges = ({agent, user}) => {
    return (
        (!agent.value.agentActive || (user.value.loginLevel !== AGENT_LEVEL)) && <div className="badges">
            {user.value?.loginLevel === COMPANY_LEVEL && <span xd-qa-type="badge" xd-qa-name="Company Admin" className="badge company-admin-badge">Company Admin</span>}
            {user.value?.loginLevel === BRANCH_LEVEL && <span xd-qa-type="badge" xd-qa-name="Office Admin" className="badge office-admin-badge">Office Admin</span>}
            {!agent.value?.agentActive && <span xd-qa-type="badge" xd-qa-name="Inactive" className="badge inactive-badge">Inactive</span>}
        </div>
    )
}

const agentName = ({agentFullName}) => {
    return (
        <div className="name" xd-qa-type="label" xd-qa-name="Agent Full Name" data-for="user-management-modal-layout" data-tip={agentFullName}>
            {agentFullName}
        </div>
    )
}

const buttonActivate = ({agent, apis, readOnly, showingDeactivationWarning}) => {
    const button = agent.value?.agentActive ? (
        <button
            disabled={readOnly?.companyAdmin}
            className={cx("deactivate",{"read-only": readOnly?.companyAdmin})}
            onClick={ (e) => {
                e.stopPropagation()
                showingDeactivationWarning.onChange(true)
            }}
            xd-qa-type="button"
            xd-qa-name="DEACTIVATE USER"
        >
            DEACTIVATE USER
        </button>
    ) : (
        <button
            disabled={readOnly?.companyAdmin}
            className={cx("reactivate",{"read-only": readOnly?.companyAdmin})}
            onClick={async () => await changeAgentStatus({apis, agent, agentActive: true})}
            xd-qa-type="button"
            xd-qa-name="REACTIVATE USER"
        >
            REACTIVATE USER
        </button>
    )

    return button
}

const headshot = ({agent}) => {
    return (
        <div className={cx("headshot",{inactive: !agent.value.agentActive})}>
            {agent.value?.imageGallery?.headshots[0]?.thumbnailUrl ? (
                <img
                    src={agent.value.imageGallery.headshots[0].thumbnailUrl}
                    alt={[agent.value.agentNameFirst, agent.value.agentNameMiddle, agent.value.agentNameLast].filter(Boolean).join(" ")}
                />
            ) : (
                <div className="headshot-placeholder">{mapFullNameAsInitials({agent: agent.value})}</div>
            )}
        </div>
    )
}

const navigation = ({active, agent, branchId, confirmCloseDialog, dataUpdated, baseUrl, menuItems, reload, routerHistory, showText = true}) => {
    return (
        <>
            {menuItems.map((menuItem) => (
                <div {...{
                    className: cx("line", {active: active === menuItem.menu}),
                    key: menuItem.label,
                    "aria-label": menuItem.label,
                    "xd-qa-type": "button",
                    "xd-qa-name": menuItem.label,
                    onClick: () => {
                        const redirectURL = addURLParameters({url: baseUrl, params:{
                            modal: "manage",
                            agent: agent.value.idAgent,
                            branch: branchId,
                            menu: menuItem.menu
                        }});
                        redirect({redirectURL, dataUpdated, confirmCloseDialog, routerHistory})
                    },
                }}>
                    <div className="icon"><FontAwesomeIcon icon={menuItem.icon}/></div>
                    {showText && <div className="label">{menuItem.label}</div>}
                </div>
            ))}
            <div {...{
                className: "line",
                "aria-label": "User Assets",
                "xd-qa-type": "button",
                "xd-qa-name": "User Assets",
                onClick: async () => {
                    let redirectURL = '';
                    if(baseUrl.indexOf('/admin/user-assets') !== -1) {
                        await reload();
                        redirectURL = baseUrl;
                    } else {
                        redirectURL = '/account/admin/user-assets?agent=' + agent.value.idAgent + "&branch=" + branchId;
                    };
                    redirect({redirectURL, dataUpdated, confirmCloseDialog, routerHistory})
                },
            }}>
                <div className="icon"><FontAwesomeIcon icon={faExternalLinkAlt}/></div>
                {showText && <div className="label">User Assets</div>}
            </div>
        </>
    )
}

const changeAgentStatus = async ({apis, agent, agentActive}) => {
    const result = await apis.next.session.updateAgent({updateFields: {idAgent: agent.value.idAgent, agentActive}});
    if (result?.errorInfo) {
        alert(result.errorInfo);
    } else {
        await agent.reload();
    }
};

const redirect = async ({redirectURL, dataUpdated, confirmCloseDialog, routerHistory}) => {
    if(dataUpdated.value) {
        const proceed = await needsConfirmation({
            confirmDialog: confirmCloseDialog,
        })
        if(proceed) {
            dataUpdated.onChange(false)
            routerHistory.push(redirectURL)
        }
    }
    else {
        routerHistory.push(redirectURL)
    }
};

const deactivationWarning = ({apis, agent, showingDeactivationWarning, daysToDelete}) => {
    return (
        <div className="deactivation-warning-options">
            <div className={
                showingDeactivationWarning.value
                ?
                "deactivation-screen hidden"
                :
                "deactivation-screen"
            }>
            </div>
            <div className={
                showingDeactivationWarning.value
                ?
                "deactivation-warning-message"
                :
                "deactivation-warning-message hidden"
            }>
                {messageDeletionWarning(daysToDelete)}
            </div>
            <div className="deactivation-warning-buttons">
                <button className={
                    showingDeactivationWarning.value
                    ?
                    "deactivation-warning-button-confirm"
                    :
                    "deactivation-warning-button-confirm hidden"
                }
                    xd-qa-type="button"
                    xd-qa-name="DEACTIVATE CONFIRM"
                    onClick={async (e) => {
                        e.stopPropagation()
                        await changeAgentStatus({apis, agent, agentActive: false})
                        showingDeactivationWarning.onChange(false)
                    }}
                >
                    CONFIRM
                </button>
                <button className={
                    showingDeactivationWarning.value
                    ?
                    "deactivation-warning-button-cancel"
                    :
                    "deactivation-warning-button-cancel hidden"
                }
                    xd-qa-type="button"
                    xd-qa-name="DEACTIVATE CANCEL"
                    onClick={(e) => {
                        e.stopPropagation()
                        showingDeactivationWarning.onChange(false)
                    }}
                >
                    CANCEL
                </button>
            </div>
        </div>
    )
}

const messageDeletionWarning = (daysToDelete) => {
    const DAYS_TO_USER_DELETION_DEFAULT = 730
    const days = daysToDelete || DAYS_TO_USER_DELETION_DEFAULT

    return(
        `Warning: this user will be permanently deleted from our database after
        ${days} days, and will not be able to be restored.`
    )
}

const getMenuItems = () => ([
    {
        menu: "general",
        icon: faPen,
        label: "User Information"
    },
    {
        menu: "customization",
        icon: faAddressCard,
        label: "Customization"
    },
    {
        menu: "security",
        icon: faShieldAlt,
        label: "Security"
    },
    {
        menu: "permissions",
        icon: faLock,
        label: "Permissions"
    },
]);